import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  SendOutlined,
  ClearOutlined,
  CheckCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, Spin, message } from 'antd';
import type { OrganizationDetail } from '../../api';
import { createOrganizationStream } from '../../api';
import { useConfig } from '../../hooks/useConfig';
import { usePageEventQueue } from '../scenarioTask/hooks/usePageEventQueue';
import OrganizationDiagramRenderer from '../scenarioTask/components/runningProcess/OrganizationDiagramRenderer';
import type { OrganizationRuntimeState } from '../scenarioTask/components/runningProcess/types';
import type { OrganizationGraphCompileResult } from '../scenarioTask/components/runningProcess/utils/organizationGraph';
import {
  applyCreateStreamItemToStates,
  isOrganizationDetailPayload,
} from './utils/diagramRuntime';
import {
  parseOrganizationStreamChunk,
  type OrganizationStreamItem,
} from './utils/stream';

/** 最后一条点亮动画落稳后，再显示“创建成功”。 */
const CREATE_VISUAL_SETTLE_MS = 260;

const CreateOrganizationPage: React.FC = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { globalConfig } = useConfig();
  const {
    createQueue,
    enqueue,
    flush,
    destroy: destroyQueue,
  } = usePageEventQueue<OrganizationStreamItem>();

  const [jsonInput, setJsonInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createFinished, setCreateFinished] = useState(false);
  const [liveDetail, setLiveDetail] = useState<OrganizationDetail | null>(null);
  const [orgStates, setOrgStates] = useState<
    Map<string, OrganizationRuntimeState>
  >(() => new Map());

  const abortControllerRef = useRef<AbortController | null>(null);
  const graphRef = useRef<OrganizationGraphCompileResult | null>(null);
  const completeTimerRef = useRef<number | null>(null);
  /** 首条非错误流消息到来后，先缓存这批事件，待图编译完成再消费。 */
  const pendingItemsRef = useRef<OrganizationStreamItem[]>([]);
  /** 表单页预读完成后保留 reader / decoder / cache，切图后继续从当前位置往下读。 */
  const pendingReaderRef =
    useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const pendingDecoderRef = useRef<TextDecoder | null>(null);
  const pendingCacheRef = useRef<{ current: string }>({ current: '' });
  /** 避免组织图编译回调重复触发时，把同一轮创建流消费启动两次。 */
  const streamStartedRef = useRef(false);

  /** 组织创建页沿用运行页的页面队列节流配置。 */
  const pageConsumeIntervalMs =
    globalConfig.demoMode === true
      ? (globalConfig.demoPageConsumeInterval ?? 0)
      : 0;

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      void pendingReaderRef.current?.cancel().catch(() => undefined);
      destroyQueue();
      if (completeTimerRef.current !== null) {
        window.clearTimeout(completeTimerRef.current);
      }
      pendingItemsRef.current = [];
      pendingReaderRef.current = null;
      pendingDecoderRef.current = null;
      pendingCacheRef.current = { current: '' };
      streamStartedRef.current = false;
    };
  }, [destroyQueue]);

  /** 清空输入框并回到编辑态。 */
  const handleClear = () => {
    setJsonInput('');
  };

  /** 填入模拟数据，方便调试。 */
  const handleFillMock = () => {
    setJsonInput(
      JSON.stringify(
        {
          roles: [
            { name: '市场专员', actions: ['解析故事编写需求'] },
            { name: '技术专家', actions: ['评估资料', '技术验收'] },
            {
              name: '汽车制造业专家',
              actions: ['获取零件重量范围', '获取零件BOM结构', '收集资料'],
            },
            { name: '场景故事编辑', actions: ['编写AI场景故事', '修改稿件'] },
            { name: '责任编辑', actions: ['审核稿件', '拟定标题'] },
          ],
          organization: {
            name: '智能原生公司',
            members: [
              {
                name: '市场部',
                members: [{ name: '吴磊', roles: ['市场专员'] }],
              },
              {
                name: '技术部',
                members: [
                  { name: '王浩', roles: ['通用设备制造业专家'] },
                  { name: '周凯', roles: ['专用设备制造业专家'] },
                  { name: '陈杰', roles: ['汽车制造业专家'] },
                  { name: '马晨', roles: ['电气机械和器材制造业专家'] },
                  { name: '郑宇', roles: ['技术专家'] },
                ],
              },
              {
                name: '编辑部',
                members: [
                  { name: '杨欣', roles: ['场景故事编辑'] },
                  { name: '王琳', roles: ['公众号编辑'] },
                  { name: '刘佳', roles: ['责任编辑'] },
                ],
              },
            ],
          },
        },
        null,
        2
      )
    );
  };

  /** 返回编辑态；若当前正在创建，则顺手取消流请求。 */
  const handleBackToEditor = () => {
    abortControllerRef.current?.abort();
    void pendingReaderRef.current?.cancel().catch(() => undefined);
    destroyQueue();
    if (completeTimerRef.current !== null) {
      window.clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
    setIsSubmitting(false);
    setIsCreating(false);
    setCreateFinished(false);
    setLiveDetail(null);
    setOrgStates(new Map());
    abortControllerRef.current = null;
    graphRef.current = null;
    pendingItemsRef.current = [];
    pendingReaderRef.current = null;
    pendingDecoderRef.current = null;
    pendingCacheRef.current = { current: '' };
    streamStartedRef.current = false;
  };

  /** 页面队列真正消费单条流事件时，再同步到组织图状态。 */
  const handleQueueItem = useCallback(
    (item: OrganizationStreamItem) => {
      const graph = graphRef.current;

      if (item.type === 'error') {
        messageApi.error(item.content || '创建组织失败');
        setIsCreating(false);
        return;
      }

      if (item.type === 'complete') {
        setCreateFinished(true);
        setIsCreating(false);
        messageApi.success('组织创建成功');
        completeTimerRef.current = window.setTimeout(() => {
          navigate('/organization');
        }, 3000);
        return;
      }

      if (!graph) return;

      setOrgStates(prev => applyCreateStreamItemToStates(prev, graph, item));
    },
    [messageApi, navigate]
  );

  /** 图编译完成后，继续消费已建立好的创建流。 */
  const startCreateStream = useCallback(async () => {
    const reader = pendingReaderRef.current;
    const decoder = pendingDecoderRef.current;
    const cache = pendingCacheRef.current;
    if (!reader || !decoder) return;

    setIsCreating(true);
    setIsSubmitting(false);
    setCreateFinished(false);
    createQueue({
      onItem: handleQueueItem,
      minIntervalMs: pageConsumeIntervalMs,
    });

    try {
      /** 完成事件必须等页面上的点亮全部消费完后，再真正触发成功提示。 */
      let pendingCompleteItem: OrganizationStreamItem | null = null;
      const enqueueStreamItems = (items: OrganizationStreamItem[]) => {
        items.forEach(item => {
          if (item.type === 'complete') {
            pendingCompleteItem = item;
            return;
          }
          enqueue(item);
        });
      };

      if (pendingItemsRef.current.length > 0) {
        enqueueStreamItems(pendingItemsRef.current);
        pendingItemsRef.current = [];
      }

      let done = false;

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;

        if (!value) continue;

        const chunk = decoder.decode(value, { stream: true });
        const items = parseOrganizationStreamChunk(chunk, cache);
        enqueueStreamItems(items);
      }

      if (cache.current.trim()) {
        enqueueStreamItems(parseOrganizationStreamChunk('', cache));
      }

      if (pendingCompleteItem) {
        /** 等待所有点亮日志在页面层消费完成，并等待最后一条点亮动画完全落稳。 */
        await flush();
        await new Promise<void>(resolve => {
          window.setTimeout(() => resolve(), CREATE_VISUAL_SETTLE_MS);
        });
        await new Promise<void>(resolve => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => resolve());
          });
        });
        enqueue(pendingCompleteItem);
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return;
      }
      messageApi.error((err as Error).message || '创建组织失败，请稍后重试');
      setIsCreating(false);
    } finally {
      pendingReaderRef.current = null;
      pendingDecoderRef.current = null;
      pendingCacheRef.current = { current: '' };
      streamStartedRef.current = false;
      abortControllerRef.current = null;
    }
  }, [
    createQueue,
    enqueue,
    flush,
    handleQueueItem,
    messageApi,
    pageConsumeIntervalMs,
  ]);

  /**
   * 提交创建组织。
   *
   * 第一轮迭代要求：接口请求必须在当前表单页发起，
   * 只有在确认开始正常流式推送后，才切到下一个组织图构建视图。
   */
  const handleCreate = async () => {
    const trimmed = jsonInput.trim();
    if (!trimmed) {
      messageApi.warning('请输入组织结构 JSON 数据');
      return;
    }

    let parsedJson: unknown;
    try {
      parsedJson = JSON.parse(trimmed);
    } catch {
      messageApi.error('JSON 格式不正确，请检查后重试');
      return;
    }

    if (!isOrganizationDetailPayload(parsedJson)) {
      messageApi.error('组织结构数据缺少 organization.name 等必要字段');
      return;
    }

    destroyQueue();
    abortControllerRef.current?.abort();
    if (completeTimerRef.current !== null) {
      window.clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
    pendingItemsRef.current = [];
    pendingReaderRef.current = null;
    pendingDecoderRef.current = null;
    pendingCacheRef.current = { current: '' };
    streamStartedRef.current = false;
    graphRef.current = null;
    setOrgStates(new Map());
    setCreateFinished(false);
    setIsSubmitting(true);
    setIsCreating(false);

    abortControllerRef.current = new AbortController();

    try {
      const body = await createOrganizationStream(
        parsedJson,
        abortControllerRef.current
      );
      const reader = body.getReader();
      const decoder = new TextDecoder();
      const cache = { current: '' };
      let done = false;
      let hasUsableEvent = false;

      while (!done && !hasUsableEvent) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;

        if (!value) continue;

        const chunk = decoder.decode(value, { stream: true });
        const items = parseOrganizationStreamChunk(chunk, cache);
        if (items.length === 0) continue;

        const firstItem = items[0];
        if (firstItem.type === 'error') {
          abortControllerRef.current?.abort();
          void reader.cancel().catch(() => undefined);
          messageApi.error(firstItem.content || '创建组织失败');
          setIsSubmitting(false);
          abortControllerRef.current = null;
          return;
        }

        pendingReaderRef.current = reader;
        pendingDecoderRef.current = decoder;
        pendingCacheRef.current = cache;
        pendingItemsRef.current = items;
        hasUsableEvent = true;
      }

      if (!hasUsableEvent) {
        messageApi.error('创建异常，未收到有效流式数据');
        setIsSubmitting(false);
        abortControllerRef.current = null;
        return;
      }

      setLiveDetail(parsedJson);
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return;
      }
      messageApi.error((err as Error).message || '创建组织失败，请稍后重试');
      setIsSubmitting(false);
      abortControllerRef.current = null;
    }
  };

  /** 子图编译完成后缓存索引，供流式点亮按 name 命中节点。 */
  const handleCompiled = useCallback(
    (graph: OrganizationGraphCompileResult) => {
      graphRef.current = graph;
      if (!pendingReaderRef.current || streamStartedRef.current) return;
      streamStartedRef.current = true;
      void startCreateStream();
    },
    [startCreateStream]
  );

  const liveStatus = createFinished
    ? {
        text: '组织构建成功',
        icon: <CheckCircleFilled />,
        className: 'border-[#d1fae5] bg-[#ecfdf5] text-[#059669]',
      }
    : {
        text: '组织构建中...',
        icon: <LoadingOutlined />,
        className: 'border-[#dbeafe] bg-[#eff6ff] text-[#2563eb]',
      };

  if (liveDetail) {
    return (
      <div className="h-full min-h-0 overflow-auto px-10 py-6">
        {contextHolder}

        <div className="mx-auto flex h-full min-h-0 max-w-[1200px] flex-col gap-4">
          <div className="flex items-center justify-between rounded-2xl bg-white px-6 py-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={handleBackToEditor}
                disabled={isCreating || createFinished}
                className="flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-lg text-[#6b7280] transition-colors hover:text-[#111827] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowLeftOutlined />
              </button>

              <div className="relative">
                <h2 className="relative top-[-6px] text-[22px] font-bold leading-none text-[#111827]">
                  {liveDetail.organization.name}
                </h2>
                <div className="absolute bottom-1 left-0 flex w-[320px] items-center gap-1 text-[11px] tracking-wide text-[#9ca3af]">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
                  LIVE ORGANIZATION BUILD VIEW
                </div>
              </div>
            </div>

            <div
              className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium ${liveStatus.className}`}
            >
              {liveStatus.icon}
              {liveStatus.text}
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden rounded-2xl bg-white shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            {isCreating && !graphRef.current ? (
              <div className="flex flex-1 items-center justify-center">
                <Spin size="large" />
              </div>
            ) : (
              <div className="h-full w-full">
                <OrganizationDiagramRenderer
                  detail={liveDetail}
                  orgStates={orgStates}
                  onCompiled={handleCompiled}
                  mode="management"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 overflow-auto px-6 py-7">
      {contextHolder}

      <div className="mx-auto w-full max-w-[860px]">
        <button
          type="button"
          onClick={() => navigate('/organization')}
          className="mb-5 flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-sm text-[#6b7280] transition-colors hover:text-[#111827]"
        >
          <ArrowLeftOutlined />
          <span>返回组织管理</span>
        </button>

        <div className="rounded-2xl bg-white p-8 shadow-[0_4px_12px_rgba(12,31,58,0.06)]">
          <h2 className="mt-[-4px] mb-2 text-xl font-bold text-[#111827]">
            构建新组织
          </h2>
          <p className="mb-4 text-sm text-[#2094f0]">
            粘贴您的组织结构 JSON 数据，系统将自动解析并模拟构建过程。
          </p>

          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs text-[#97a0af]">
                组织结构数据 (JSON)
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleFillMock}
                  disabled={isSubmitting}
                  className="cursor-pointer border-none bg-transparent p-0 text-xs text-[#2094f0] transition-colors hover:text-[#1a7fd4] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  填入模拟数据
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={isSubmitting}
                  className="flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-xs text-[#2094f0] transition-colors hover:text-[#1a7fd4] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ClearOutlined />
                  清空
                </button>
              </div>
            </div>
            <textarea
              value={jsonInput}
              onChange={e => setJsonInput(e.target.value)}
              disabled={isSubmitting}
              placeholder={`{\n  "organization": {\n    "name": "示例公司",\n    "members": []\n  }\n}`}
              className="h-[380px] w-full resize-none rounded-lg border border-[#e5e7eb] bg-[#fafbfc] p-4 font-mono text-sm text-[#374151] outline-none transition-colors placeholder:text-[#c2cad6] focus:border-[#2094f0] disabled:opacity-60"
            />
          </div>

          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={isSubmitting}
            onClick={handleCreate}
            className="!h-12 !w-full !rounded-xl !border-none !bg-gradient-to-r !from-[#45b5f0] !to-[#2094f0] !text-base !font-semibold !shadow-md hover:!shadow-lg"
          >
            {isSubmitting ? '正在提交创建请求...' : '开始创建组织'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganizationPage;
